import { useState } from "react";
import { useBodyMeasurementEntries } from "@/hooks/useBodyMeasurements";
import {
  formatEntryDate,
  formatMeasurementValue,
  localDateOf,
  parseMeasurementInput,
} from "@/lib/body-measurements";
import type {
  BiologicalSex,
  EnergyExpenditureEstimate,
  MealLogSummaryConfig,
} from "@/lib/schema";
import {
  ACTIVITY_LEVELS,
  ageFromBirthYear,
  DEFAULT_ACTIVITY_FACTOR,
  estimatedExpenditure,
  formatKcal,
} from "./summary";

interface Props {
  config: MealLogSummaryConfig;
  onChange: (patch: Partial<MealLogSummaryConfig>) => void;
  onApply: (expenditure: number) => void;
}

/** The earliest date the range read can start from — before any measurement. */
const EPOCH = "1900-01-01";

export function ExpenditureCalculator({ config, onChange, onApply }: Props) {
  const today = new Date();
  const stored = config.expenditureEstimate;

  // The weight you last calculated with is not a fact worth preserving; the
  // weight you are now is. So it is re-read on open rather than restored.
  const { entries } = useBodyMeasurementEntries(EPOCH, localDateOf(today));
  const latestWeight = [...entries]
    .reverse()
    .find((e) => typeof e.values.weight === "number");

  // Derived rather than seeded into state: the measurement read is async, so
  // a lazy useState initialiser would run before any entry has arrived and the
  // prefill would never land. Null means untouched — once the user types
  // anything, including clearing the field, their value wins for good.
  const [weightEdit, setWeightEdit] = useState<string | null>(null);
  const prefilledWeight = latestWeight
    ? formatMeasurementValue(latestWeight.values.weight as number)
    : stored
      ? formatMeasurementValue(stored.weightKilograms)
      : "";
  const weightText = weightEdit ?? prefilledWeight;
  const [heightText, setHeightText] = useState(() =>
    stored ? String(stored.heightCentimeters) : "",
  );
  const [ageText, setAgeText] = useState(() =>
    stored ? String(ageFromBirthYear(stored.birthYear, today)) : "",
  );
  const [sex, setSex] = useState<BiologicalSex>(stored?.sex ?? "male");
  const [activityFactor, setActivityFactor] = useState(
    stored?.activityFactor ?? DEFAULT_ACTIVITY_FACTOR,
  );

  const age = Number(ageText.trim());
  const inputs: Partial<EnergyExpenditureEstimate> = {
    weightKilograms: parseMeasurementInput(weightText) ?? Number.NaN,
    heightCentimeters: parseMeasurementInput(heightText) ?? Number.NaN,
    birthYear:
      ageText.trim() !== "" && Number.isInteger(age)
        ? today.getFullYear() - age
        : Number.NaN,
    sex,
    activityFactor,
  };
  const result = estimatedExpenditure(inputs, today);

  const apply = () => {
    if (!result) return;
    onChange({
      expenditureEstimate: inputs as EnergyExpenditureEstimate,
    });
    onApply(result.expenditure);
  };

  return (
    <div
      className="expenditure-calculator"
      data-testid="expenditure-calculator"
    >
      <div className="calculator-fields">
        <label className="calculator-field">
          <span>Vekt (kg)</span>
          <input
            className="input"
            type="text"
            inputMode="decimal"
            value={weightText}
            onChange={(e) => setWeightEdit(e.target.value)}
            data-testid="calculator-weight-input"
          />
        </label>
        <label className="calculator-field">
          <span>Høyde (cm)</span>
          <input
            className="input"
            type="text"
            inputMode="decimal"
            value={heightText}
            onChange={(e) => setHeightText(e.target.value)}
            data-testid="calculator-height-input"
          />
        </label>
        <label className="calculator-field">
          <span>Alder (år)</span>
          <input
            className="input"
            type="text"
            inputMode="numeric"
            value={ageText}
            onChange={(e) => setAgeText(e.target.value)}
            data-testid="calculator-age-input"
          />
        </label>
        <label className="calculator-field">
          <span>Kjønn</span>
          <select
            className="input"
            value={sex}
            onChange={(e) => setSex(e.target.value as BiologicalSex)}
            data-testid="calculator-sex-select"
          >
            <option value="male">Mann</option>
            <option value="female">Kvinne</option>
          </select>
        </label>
        <label className="calculator-field calculator-field-wide">
          <span>Aktivitetsnivå</span>
          <select
            className="input"
            value={String(activityFactor)}
            onChange={(e) => setActivityFactor(Number(e.target.value))}
            data-testid="calculator-activity-select"
          >
            {ACTIVITY_LEVELS.map((level) => (
              <option key={level.factor} value={String(level.factor)}>
                {level.label} (×{formatMeasurementValue(level.factor, 3)})
              </option>
            ))}
          </select>
        </label>
      </div>
      {latestWeight ? (
        <p className="hint" data-testid="calculator-weight-source">
          Vekt hentet fra måling {formatEntryDate(latestWeight.date)}.
        </p>
      ) : null}
      {/* Both steps on screen so the number is never a black box. */}
      <p className="calculator-result-line">
        <span data-testid="calculator-resting-rate">
          Hvilestoffskifte:{" "}
          {result ? `${formatKcal(result.restingRate)} kcal` : "—"}
        </span>
        <span data-testid="calculator-result">
          {result
            ? `× ${formatMeasurementValue(activityFactor, 3)} = ${formatKcal(result.expenditure)} kcal/dag`
            : "Fyll inn alle feltene."}
        </span>
      </p>
      <button
        type="button"
        className="btn"
        disabled={!result}
        onClick={apply}
        data-testid="apply-calculator-button"
      >
        Bruk
      </button>
    </div>
  );
}
